/**
 * Learning hub landing page.
 *
 * Entry point for `/learning`: choose grammar or common sentences (Redemittel).
 * Phrase content loads only on `/learning/sentences`.
 *
 * Route: `/learning`
 * Requires authentication: Yes
 *
 * @module LearningHub
 */

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { StandardPageShell } from '@/components/layout/StandardPageShell';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { LEARNING_CONTENT_DESTINATIONS } from '@/data/learningNavigation';

function defaultTitleForContentType(id: 'grammar' | 'sentences'): string {
  return id === 'grammar' ? 'Grammar' : 'Common Sentences';
}

function defaultDescriptionForContentType(id: 'grammar' | 'sentences'): string {
  return id === 'grammar'
    ? 'Master German grammar step by step.'
    : 'Essential phrases and expressions for every level (Redemittel).';
}

export default function LearningHub() {
  const { t } = useTranslation();

  return (
    <StandardPageShell
      title={t('learning.hub.title', { defaultValue: 'Learning' })}
      headerRight={<LanguageSwitcher />}
    >
      <div className="space-y-6">
        <p className="font-ui text-sm text-muted-foreground max-w-2xl">
          {t('learning.hub.subtitle', {
            defaultValue: 'Grammar and common phrases (Redemittel) — open a section to continue.',
          })}
        </p>

        <ul className="grid gap-4 sm:grid-cols-2 list-none p-0 m-0 max-w-3xl">
          {LEARNING_CONTENT_DESTINATIONS.map((dest) => {
            const Icon = dest.icon;
            const title = t(`learning.sidebar.types.${dest.id}`, {
              defaultValue: defaultTitleForContentType(dest.id),
            });
            const description = t(`learning.hub.cards.${dest.id}`, {
              defaultValue: defaultDescriptionForContentType(dest.id),
            });

            return (
              <li key={dest.id}>
                <Link
                  to={dest.path}
                  className="group block h-full rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <Card
                    className={cn(
                      'h-full transition-colors border-border',
                      'group-hover:border-primary/40 group-hover:bg-accent/30'
                    )}
                  >
                    <CardHeader className="space-y-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <Icon className="h-5 w-5" aria-hidden />
                      </div>
                      <CardTitle className="font-ui text-lg">{title}</CardTitle>
                      <CardDescription className="font-ui text-sm">{description}</CardDescription>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <span className="font-ui text-sm font-medium text-primary">
                        {t('learning.hub.openSection', { defaultValue: 'Open' })} →
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </StandardPageShell>
  );
}
